import { useCallback, useMemo, useRef, useState } from 'react'
import type { FlowDefinition, FlowNode } from '@shared/schema/flow'
import type { StepAnswers, AnswerValue } from '@shared/schema/answers'
import type { SimulatorSnapshot } from '@shared/engine/simulate'
import { createSimulator } from '@shared/engine/simulate'
import type { FieldError } from '@shared/engine/fields'
import { getRenderBlocks, getNodeFields } from '@shared/engine/fields'
import { pruneHiddenAnswers } from '@shared/engine/visibility'
import { CheckUniqueResponseSchema, CheckLookupResponseSchema } from '@shared/schema/api'
import { DEFAULT_LOOKUP_MATCH_MESSAGE } from '@shared/schema/blocks'
import { api } from '../api/client'

/**
 * เครื่องเดินขั้นตอนฝั่ง UI — ห่อ simulator ของ engine กลางเอาไว้
 * ตรรกะการเลือกเส้นทางทั้งหมดอยู่ใน engine ไม่มีการตัดสินใจซ้ำในชั้น UI
 * (นี่คือเหตุผลที่โหมดจำลองใน builder กับการใช้งานจริงให้ผลตรงกันเสมอ)
 */

export type RunnerStatus = 'answering' | 'finished' | 'error'

export type RunnerState = {
  node: FlowNode
  stepKey: string
  seq: number
  answers: StepAnswers
  errors: Record<string, string>
  progress: number
  canGoBack: boolean
  status: RunnerStatus
  errorMessage: string | null
}

export type StepCommit = {
  submissionId: string
  stepKey: string
  nodeId: string
  seq: number
  answers: StepAnswers
  answeredAt: number
}

export type UseRunnerOptions = {
  definition: FlowDefinition
  workflowId: string
  submissionId: string
  restore?: SimulatorSnapshot | undefined
  /** เรียกเมื่อคำตอบของขั้นตอนหนึ่งถูกยืนยัน (บันทึกลงคิว/เซิร์ฟเวอร์) */
  onCommitStep: (commit: StepCommit) => void
  /** เรียกเมื่อทำแบบสอบถามจบ — incomplete = true เมื่อกดจบสัมภาษณ์ก่อนถึงขั้นตอนจบจริง */
  onComplete: (finalSeq: number, incomplete: boolean) => void
  /** เรียกทุกครั้งที่สถานะเปลี่ยน เพื่อเก็บไว้กู้คืนตอนรีเฟรช */
  onSnapshot?: (snapshot: SimulatorSnapshot) => void
  /** เรียกเมื่อค่าที่ตอบตรงกับชุดข้อมูลอ้างอิงเก่า (เช่น ร้านนี้เคยสำรวจไปแล้วปีที่แล้ว) — แจ้งเตือนเท่านั้น ไม่บล็อกการตอบ */
  onLookupMatch?: (message: string, data: Record<string, string>) => void
}

function errorsToMap(errors: FieldError[]): Record<string, string> {
  return Object.fromEntries(errors.map((error) => [error.fieldName, error.message]))
}

export function useRunner(options: UseRunnerOptions) {
  const { definition, workflowId, submissionId, restore, onCommitStep, onComplete, onSnapshot, onLookupMatch } =
    options

  const simulator = useMemo(
    () => createSimulator(definition, undefined, restore),
    // สร้างครั้งเดียวต่อ definition — ไม่สร้างใหม่ทุก render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [definition],
  )

  const [draft, setDraft] = useState<StepAnswers>(() => simulator.currentAnswers())
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [status, setStatus] = useState<RunnerStatus>(() =>
    simulator.isFinished() ? 'finished' : 'answering',
  )
  const [, forceRender] = useState(0)
  const busy = useRef(false)

  const sync = useCallback(() => {
    setDraft(simulator.currentAnswers())
    setErrors({})
    forceRender((n) => n + 1)
    onSnapshot?.(simulator.snapshot())
  }, [simulator, onSnapshot])

  const setValue = useCallback((fieldName: string, value: AnswerValue) => {
    setDraft((current) => ({ ...current, [fieldName]: value }))
    setErrors((current) => {
      if (!(fieldName in current)) return current
      const next = { ...current }
      delete next[fieldName]
      return next
    })
  }, [])

  const next = useCallback(async () => {
    if (busy.current || simulator.isFinished()) return
    busy.current = true
    try {
      const node = simulator.current()

      // ฟิลด์ที่ตั้ง "ห้ามข้อมูลซ้ำ" — เช็คกับเซิร์ฟเวอร์ก่อนไปต่อ (ทำได้เฉพาะตอนออนไลน์
      // ตอนออฟไลน์จะปล่อยผ่านไปก่อน แล้วเซิร์ฟเวอร์จะตรวจซ้ำอีกทีตอน sync ขึ้นจริง)
      if (typeof navigator !== 'undefined' && navigator.onLine) {
        const dupErrors: Record<string, string> = {}
        for (const field of getNodeFields(node)) {
          if (!field.unique) continue
          const value = draft[field.name]
          if (typeof value !== 'string' && typeof value !== 'number') continue
          const text = String(value).trim()
          if (!text) continue
          try {
            const params = new URLSearchParams({
              workflowId,
              fieldName: field.name,
              value: text,
              excludeSubmissionId: submissionId,
            })
            const checked = await api.get(
              `/api/submissions/check-unique?${params.toString()}`,
              CheckUniqueResponseSchema,
            )
            if (checked.duplicate) {
              dupErrors[field.name] = `"${field.label}" นี้มีอยู่ในระบบแล้ว ห้ามซ้ำ`
            }
          } catch {
            // เช็คไม่ได้ (เช่นเน็ตเพิ่งหลุดกลางทาง) — ปล่อยผ่าน เดี๋ยวเซิร์ฟเวอร์ตรวจซ้ำตอน sync
          }
        }
        if (Object.keys(dupErrors).length > 0) {
          setErrors((current) => ({ ...current, ...dupErrors }))
          setErrorMessage(null)
          return
        }

        // ฟิลด์ที่ตั้ง "เช็คกับฐานข้อมูลเก่า" — แจ้งเตือนทันทีถ้าตรง (ไม่บล็อกการตอบ)
        // การบันทึกผลจริงลงงานนี้เกิดที่ฝั่งเซิร์ฟเวอร์ตอน PUT step เสมอ (ออฟไลน์ก็ใช้ได้ ไปเช็คตอน sync)
        for (const field of getNodeFields(node)) {
          if (!field.checkAgainstLookup) continue
          const value = draft[field.name]
          if (typeof value !== 'string' && typeof value !== 'number') continue
          const text = String(value).trim()
          if (!text) continue
          try {
            const params = new URLSearchParams({ workflowId, value: text })
            const checked = await api.get(
              `/api/submissions/check-lookup?${params.toString()}`,
              CheckLookupResponseSchema,
            )
            if (checked.matched && checked.data) {
              onLookupMatch?.(field.lookupMatchMessage.trim() || DEFAULT_LOOKUP_MATCH_MESSAGE, checked.data)
            }
          } catch {
            // เช็คไม่ได้ (เช่นเน็ตเพิ่งหลุดกลางทาง) — ปล่อยผ่าน เดี๋ยวเซิร์ฟเวอร์เช็คอีกทีตอน sync
          }
        }
      }

      const stepKey = simulator.currentStepKey()
      const seq = simulator.path().length
      const answeredAt = Date.now()

      // ★ ตัดคำตอบของช่องที่ถูกซ่อนอยู่ทิ้งก่อน แล้วใช้ชุดที่สะอาดแล้วทั้งเดินเส้นทางและส่งขึ้นเซิร์ฟเวอร์
      // (เคยพลาดตรงนี้: ส่ง draft ดิบไปให้ onCommitStep ทำให้ข้อความที่พิมพ์ค้างไว้ตอนช่องยังโผล่
      //  ติดไปกับข้อมูลจริงทั้งที่ผู้ตอบเปลี่ยนใจไปแล้ว)
      const cleaned = pruneHiddenAnswers(getRenderBlocks(node), draft)

      const result = simulator.submit(cleaned)

      switch (result.status) {
        case 'invalid':
          setErrors(errorsToMap(result.errors))
          setErrorMessage(null)
          return
        case 'noMatch':
          setErrorMessage(
            'คำตอบนี้ไม่ตรงกับเส้นทางใดที่ออกแบบไว้ กรุณาแจ้งผู้ดูแลระบบ (ข้อมูลที่กรอกไว้ยังอยู่)',
          )
          setStatus('error')
          return
        case 'brokenEdge':
          setErrorMessage('เส้นทางในแบบสอบถามชี้ไปยังขั้นตอนที่ถูกลบ กรุณาแจ้งผู้ดูแลระบบ')
          setStatus('error')
          return
        case 'tooManySteps':
          setErrorMessage('แบบสอบถามนี้วนกลับมากเกินไป กรุณาแจ้งผู้ดูแลระบบ')
          setStatus('error')
          return
        default:
          break
      }

      onCommitStep({ submissionId, stepKey, nodeId: node.id, seq, answers: cleaned, answeredAt })
      setErrorMessage(null)

      if (result.status === 'finished') {
        setStatus('finished')
        onComplete(simulator.path().length, false)
      }
      sync()
    } finally {
      busy.current = false
    }
  }, [draft, simulator, workflowId, submissionId, onCommitStep, onComplete, onLookupMatch, sync])

  /** หน้าจบ: กดยืนยันเพื่อปิดงาน — incomplete = true เมื่อเป็นการจบก่อนถึงขั้นตอนจบจริง */
  const finish = useCallback(
    (incomplete = false) => {
      if (simulator.isFinished()) return
      simulator.finish()
      setStatus('finished')
      onComplete(simulator.path().length, incomplete)
      sync()
    },
    [simulator, onComplete, sync],
  )

  const back = useCallback(() => {
    if (!simulator.back()) return
    setStatus('answering')
    setErrorMessage(null)
    sync()
  }, [simulator, sync])

  const state: RunnerState = {
    node: simulator.current(),
    stepKey: simulator.currentStepKey(),
    seq: simulator.path().length,
    answers: draft,
    errors,
    progress: simulator.progress(),
    canGoBack: simulator.canGoBack(),
    status,
    errorMessage,
  }

  return {
    state,
    blocks: getRenderBlocks(simulator.current()),
    setValue,
    next,
    back,
    finish,
    snapshot: () => simulator.snapshot(),
  }
}
